import * as React from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/utils';
import { containsMarkdownSyntax } from '@/utils/markdown';

/**
 * Regex to split text by URLs or @mention patterns.
 * Captures:
 *   - URLs: https?://...
 *   - Mentions: @username (alphanumeric, underscores, dots, hyphens)
 */
const MENTION_OR_URL_REGEX =
  /((?:https?:\/\/[^\s]+)|(?:@[a-zA-Z0-9_]+(?:[.\-@][a-zA-Z0-9_]+)*))/g;

/**
 * Renders text with @mention highlighting and clickable URLs.
 * Splits the input on MENTION_OR_URL_REGEX, styling each segment.
 * Reused wherever mention-aware rendering is needed (timeline, events, etc.).
 */
function renderMentionAwareLine(line: string): React.ReactNode {
  const parts = line.split(MENTION_OR_URL_REGEX);

  return (
    <>
      {parts.map((part, partIdx) => {
        if (part.match(/^https?:\/\/[^\s]+$/)) {
          return (
            <a
              key={partIdx}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline decoration-blue-300/50 underline-offset-2 font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        if (part.match(/^@[a-zA-Z0-9_]+(?:[.\-@][a-zA-Z0-9_]+)*$/)) {
          return (
            <span
              key={partIdx}
              className="inline-flex items-center rounded-md bg-blue-50 px-1 py-0.5 text-blue-700 font-medium text-[10px] ring-1 ring-inset ring-blue-700/10 mx-0.5 align-baseline"
            >
              {part}
            </span>
          );
        }
        return <span key={partIdx}>{part}</span>;
      })}
    </>
  );
}

export function renderTextWithMentions(text: string): React.ReactNode {
  if (!text) return null;

  const lines = text.split('\n');

  return (
    <>
      {lines.map((line, lineIdx) => (
        <React.Fragment key={lineIdx}>
          {line ? renderMentionAwareLine(line) : null}
          {lineIdx < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
}

// Declare MarkdownLink and MarkdownPre before renderMarkdownNodeWithMentions
// so they can be referenced in the skip-list for custom component types.

const MarkdownLink: React.FC<React.ComponentProps<'a'>> = ({
  href,
  children,
  ...props
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-600 hover:text-blue-800 underline"
    {...props}
  >
    {children}
  </a>
);

const MarkdownPre: React.FC<React.ComponentProps<'pre'>> = ({
  children,
  ...props
}) => (
  <pre
    className="bg-slate-50 border border-slate-200 rounded p-2 text-xs overflow-x-auto"
    {...props}
  >
    {children}
  </pre>
);

function renderMarkdownNodeWithMentions(node: React.ReactNode): React.ReactNode {
  if (typeof node === 'string') {
    // Use renderMentionAwareLine (not renderTextWithMentions) to avoid
    // splitting on \n and inserting <br/> — the markdown parser already
    // handles line breaks, so we only need mention/URL styling here.
    return renderMentionAwareLine(node);
  }

  if (Array.isArray(node)) {
    return node.map((child, idx) => (
      <React.Fragment key={idx}>{renderMarkdownNodeWithMentions(child)}</React.Fragment>
    ));
  }

  if (!React.isValidElement(node)) {
    return node;
  }

  // Don't rewrite links or code blocks — check both native HTML tags and
  // custom component replacements (e.g. MarkdownLink, MarkdownPre) since
  // ReactMarkdown maps tags to custom components via the `components` prop.
  if (typeof node.type === 'string') {
    if (node.type === 'a' || node.type === 'code' || node.type === 'pre') {
      return node;
    }
  }

  if (node.type === MarkdownLink || node.type === MarkdownPre) {
    return node;
  }

  if (!node.props?.children) {
    return node;
  }

  return React.cloneElement(
    node,
    node.props,
    renderMarkdownNodeWithMentions(node.props.children),
  );
}

const MentionAwareP: Components['p'] = ({ children, ...props }) => (
  <p {...props}>{renderMarkdownNodeWithMentions(children)}</p>
);

const MentionAwareLi: Components['li'] = ({ children, ...props }) => (
  <li {...props}>{renderMarkdownNodeWithMentions(children)}</li>
);

interface MarkdownTextProps {
  content: string;
  className?: string;
  plainTextClassName?: string;
  markdownClassName?: string;
  components?: Components;
}

export const MarkdownText: React.FC<MarkdownTextProps> = ({
  content,
  className,
  plainTextClassName,
  markdownClassName,
  components,
}) => {
  const hasMarkdownSyntax = React.useMemo(
    () => containsMarkdownSyntax(content),
    [content],
  );

  if (!hasMarkdownSyntax) {
    return (
      <div className={cn('whitespace-pre-wrap', className, plainTextClassName)}>
        {renderTextWithMentions(content)}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'prose prose-sm max-w-none text-xs [&_*]:text-xs prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:text-xs prose-headings:font-semibold prose-headings:my-1.5 prose-pre:text-xs prose-pre:bg-slate-100 prose-pre:px-2 prose-pre:py-1 prose-pre:border prose-pre:border-slate-200 prose-pre:rounded',
        className,
        markdownClassName,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: MarkdownLink,
          pre: MarkdownPre,
          p: MentionAwareP,
          li: MentionAwareLi,
          ...components,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
